import {
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { motion, useScroll } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const StyledAppBar = styled(motion.div)(({ theme, transparent }) => ({
  backgroundColor: transparent ? 'transparent' : theme.palette.background.default,
  boxShadow: transparent ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.03)',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
  transition: 'all 0.3s ease',
  height: '80px',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    height: '70px',
    padding: theme.spacing(1),
  }
}));

const Logo = styled(Typography)(({ theme, isTransparent }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: isTransparent ? 'white' : theme.palette.primary.main,
  textDecoration: 'none',
  marginRight: 'auto',
  fontFamily: "'Poppins', sans-serif",
  letterSpacing: '-0.5px',
  '&:hover': {
    color: isTransparent ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.dark,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  }
}));

const NavButton = styled(Button)(({ theme, isTransparent }) => ({
  marginLeft: theme.spacing(2),
  padding: theme.spacing(0.5, 2),
  backgroundColor: isTransparent ? 'rgba(255, 255, 255, 0.1)' : 'white',
  color: isTransparent ? 'white' : theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  transition: 'all 0.3s ease',
  border: `1px solid ${isTransparent ? 'transparent' : theme.palette.primary.main}`,
  boxShadow: isTransparent ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.05)',
  height: '36px',
  minWidth: '80px',

  '&:hover': {
    backgroundColor: isTransparent 
      ? 'rgba(255, 255, 255, 0.2)'
      : theme.palette.primary.main,
    color: isTransparent ? 'white' : 'white',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },

  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.85rem',
    height: '32px',
    minWidth: '70px',
  }
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 240,
    backgroundColor: theme.palette.background.default,
  },
}));

function Navbar() {
  const { logout } = useFirebaseAuth();
  const user = useSelector(state => state.auth.user);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const isHome = location.pathname === '/';
  const isTransparent = isHome && !isScrolled;

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    return scrollY.onChange(() => setIsScrolled(scrollY.get() > 50));
  }, [scrollY]);

  const navItems = [
    { 
      text: 'About', 
      scrollTo: 'about-section',
      isScroll: true 
    },
    { 
      text: 'Contact', 
      scrollTo: 'contact-section',
      isScroll: true 
    },
  ];

  const handleScrollTo = (elementId) => {
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
    if (mobileOpen) {
      handleMobileMenuToggle();
    }
  };

  const renderNavItems = () => (
    <>
      <NavButton
        onClick={() => handleScrollTo('about-section')}
        isTransparent={isTransparent}
        sx={{
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            width: 0,
            height: '2px',
            backgroundColor: isTransparent ? 'white' : 'primary.main',
            transition: 'all 0.3s ease',
            transform: 'translateX(-50%)',
          },
          '&:hover::after': {
            width: '80%',
          }
        }}
      >
        About
      </NavButton>
      <NavButton
        onClick={() => handleScrollTo('contact-section')}
        isTransparent={isTransparent}
        sx={{
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            width: 0,
            height: '2px',
            backgroundColor: isTransparent ? 'white' : 'primary.main',
            transition: 'all 0.3s ease',
            transform: 'translateX(-50%)',
          },
          '&:hover::after': {
            width: '80%',
          }
        }}
      >
        Contact
      </NavButton>
    </>
  );

  const renderAuthButtons = () => (
    <>
      {user ? (
        <Avatar
          onClick={handleMenu}
          sx={{ 
            ml: 2, 
            cursor: 'pointer',
            bgcolor: 'primary.main',
            width: 40,
            height: 40,
          }}
        >
          {user.email[0].toUpperCase()}
        </Avatar>
      ) : (
        <>
          <NavButton
            variant="outlined"
            component={RouterLink}
            to="/login"
            isTransparent={isTransparent}
            sx={{
              borderColor: isTransparent ? 'white' : 'primary.main',
            }}
          >
            Login
          </NavButton>
          <NavButton
            variant="contained"
            component={RouterLink}
            to="/register"
            sx={{ 
              ml: 2,
              bgcolor: isTransparent ? 'white' : 'primary.main',
              color: isTransparent ? 'primary.main' : 'white',
            }}
          >
            Register
          </NavButton>
        </>
      )}
    </>
  );

  const renderMobileMenuItems = () => (
    <List>
      {navItems.map((item) => (
        <ListItem 
          key={item.text}
          button
          onClick={() => item.isScroll ? handleScrollTo(item.scrollTo) : navigate(item.path)}
          sx={{
            my: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(139, 0, 0, 0.04)',
            }
          }}
        >
          <ListItemText 
            primary={item.text} 
            primaryTypographyProps={{
              fontWeight: 600,
              color: 'primary.main'
            }}
          />
        </ListItem>
      ))}
      {!user && (
        <>
          <ListItem 
            button
            onClick={() => {
              handleMobileMenuToggle();
              navigate('/login');
            }}
          >
            <ListItemText primary="Login" />
          </ListItem>
          <ListItem 
            button
            onClick={() => {
              handleMobileMenuToggle();
              navigate('/register');
            }}
          >
            <ListItemText primary="Register" />
          </ListItem>
        </>
      )}
    </List>
  );

  return (
    <StyledAppBar
      initial={{ height: 80 }}
      animate={{ 
        height: isScrolled ? 64 : 80,
        backgroundColor: isTransparent 
          ? 'transparent' 
          : (isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 1)'),
        backdropFilter: isScrolled ? 'blur(10px)' : 'none',
      }}
      transparent={isTransparent}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ width: '100%', justifyContent: 'space-between' }}>
          <Logo
            component={RouterLink}
            to="/"
            isTransparent={isTransparent}
            variant="h1"
          >
            RentWheels
          </Logo>

          {isMobile ? (
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuToggle}
                sx={{ ml: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <MobileDrawer
                anchor="right"
                open={mobileOpen}
                onClose={handleMobileMenuToggle}
                PaperProps={{
                  sx: {
                    width: '250px',
                    paddingTop: '20px'
                  }
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <IconButton onClick={handleMobileMenuToggle}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  {renderMobileMenuItems()}
                </Box>
              </MobileDrawer>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}>
              {renderNavItems()}
              {renderAuthButtons()}
            </Box>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem 
              component={RouterLink} 
              to="/profile"
              onClick={handleClose}
            >
              Profile
            </MenuItem>
            <MenuItem 
              component={RouterLink} 
              to="/bookings"
              onClick={handleClose}
            >
              My Bookings
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
}

export default Navbar; 